import React, { useMemo, useState } from 'react';
import {
  Users,
  UserPlus,
  Shield,
  Clock,
  Mail,
  Building,
  ChevronDown
} from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import CoordinatePickerMap from './common/CoordinatePickerMap';
import StatCard from './StatCard';
import DataTable from './DataTable';
import Button from './common/Button';
import LanguageSelector from './common/LanguageSelector';
import ModuleSettingsPanel from './ModuleSettingsPanel';
import { useConstruction } from '../context/ConstructionContext';
import { t } from '../i18n';

const Administration = () => {
  const {
    currentUser,
    users,
    parks,
    costCenters,
    categories,
    renewablePortfolio,
    preferences,
    updatePreferences,
    addUser,
    updateUserProfile,
    updateUserRole,
    updateUserAllowedPages,
    createPark,
    addParkPackage,
    addParkArea,
    createCategory,
    updatePark,
    resetAppToZero,
    loadEnergyTemplates,
    auditTrail
  } = useAppContext();
  const { resetConstruction } = useConstruction();
  const isSuperUser = currentUser?.profile === 'super_user';
  const roleOptions = [
    { value: 'super_user', label: t('Super Usuario') },
    { value: 'purchasing_manager', label: t('Gerencia de Compras') },
    { value: 'purchase_controller', label: t('Purchase Controller') },
    { value: 'comprador', label: t('Comprador') },
    { value: 'project_manager', label: t('Project Manager') },
    { value: 'area_manager', label: t('Gerente de Área') },
    { value: 'contract_manager', label: t('Contract Manager') },
    { value: 'abogado', label: t('Legal (Abogado)') },
    { value: 'office_staff', label: t('Administración (Oficina)') },
    { value: 'field_technician', label: t('Técnico (Terreno)') }
  ];
  const pagesCatalog = [
    { module: 'Proyectos', pages: ['projects', 'projects-budget', 'projects-timeline', 'projects-risks', 'projects-milestones', 'projects-status'] },
    { module: 'Desarrollo', pages: ['development-portfolio', 'development-geo', 'development-permits', 'development-assistant', 'development-alerts', 'development-reporting', 'development-team'] },
    { module: 'Proveedores & Compliance', pages: ['suppliers-overview', 'suppliers-legal', 'suppliers-compliance', 'suppliers-history', 'suppliers-orders'] },
    { module: 'Abastecimiento', pages: ['procurement-requisitions', 'procurement-tenders', 'procurement-orders', 'procurement-approvals', 'procurement-inventory', 'procurement-shipments', 'procurement-suppliers'] },
    { module: 'Sistema', pages: ['dashboard', 'reports', 'admin', 'api'] }
  ];
  const allPages = pagesCatalog.flatMap((item) => item.pages);
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    role: 'comprador',
    department: '',
    phone: ''
  });
  const [selectedUserId, setSelectedUserId] = useState(users[0]?.id || '');
  const [editingParkId, setEditingParkId] = useState('');
  const [editParkOpen, setEditParkOpen] = useState(false);
  const [editParkError, setEditParkError] = useState('');

  const [parkForm, setParkForm] = useState({
    name: '',
    code: '',
    managerId: '',
    technology: 'Solar FV',
    region: '',
    commune: '',
    mw: '',
    lat: '',
    lng: ''
  });
  const [editParkForm, setEditParkForm] = useState({
    name: '',
    code: '',
    managerId: '',
    technology: 'Solar FV',
    region: '',
    commune: '',
    mw: '',
    lat: '',
    lng: ''
  });
  const [areaForm, setAreaForm] = useState({
    parkId: '',
    name: '',
    areaManagerId: '',
    costCenterCode: '',
    costCenterName: '',
    costCenterDescription: ''
  });
  const [packageForm, setPackageForm] = useState({ parkId: '', code: '', name: '' });
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    type: 'material',
    budgetCategory: 'otros'
  });

  const totalAudits = auditTrail.length;

  const roleCounters = useMemo(() => {
    return users.reduce(
      (acc, user) => {
        const profile = user.profile || 'operativo';
        acc[profile] = (acc[profile] || 0) + 1;
        return acc;
      },
      {
        operativo: 0,
        gerente: 0,
        director: 0,
        super_user: 0
      }
    );
  }, [users]);

  const selectedUser = useMemo(
    () => users.find((user) => user.id === selectedUserId) || null,
    [users, selectedUserId]
  );
  const selectedAccess = useMemo(
    () => new Set(selectedUser?.allowedPages || []),
    [selectedUser]
  );

  const handleSubmit = (event) => {
    event.preventDefault();
    addUser({
      ...formState
    });
    setFormState({
      name: '',
      email: '',
      role: 'comprador',
      department: '',
      phone: ''
    });
  };

  const parkById = useMemo(() => {
    const map = new Map();
    (parks || []).forEach((park) => map.set(park.id, park));
    return map;
  }, [parks]);

  const handleOpenEditPark = (parkId) => {
    const park = parkById.get(parkId);
    if (!park) return;
    const dev = (renewablePortfolio || []).find((project) => project.parkId === parkId) || null;
    // We store coordinates on the development (renewables) project; the edit will persist to both.
    // When no coordinates exist, keep empty.
    setEditingParkId(parkId);
    setEditParkError('');
    setEditParkOpen(true);
    setEditParkForm({
      name: park.name || '',
      code: park.code || '',
      managerId: park.managerId || '',
      technology: dev?.technology || 'Solar FV',
      region: dev?.region || '',
      commune: dev?.commune || '',
      mw: dev?.mw ? String(dev.mw) : '',
      lat: dev?.coordinates?.lat !== undefined && dev?.coordinates?.lat !== null ? String(dev.coordinates.lat) : '',
      lng: dev?.coordinates?.lng !== undefined && dev?.coordinates?.lng !== null ? String(dev.coordinates.lng) : ''
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">{t('Administración y Auditoría')}</h1>
        <p className="text-sm text-slate-500">{t(
          'Alta de usuarios, asignación de roles y bitácora de acciones para auditoría interna.'
        )}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard title={t('Usuarios activos')} value={users.length} icon={Users} />
        <StatCard title={t('Compradores')} value={roleCounters.operativo} icon={Shield} />
        <StatCard title={t('Gerentes')} value={roleCounters.gerente} icon={Shield} />
        <StatCard title={t('Directores')} value={roleCounters.director} icon={Shield} />
        <StatCard title={t('Super Usuarios')} value={roleCounters.super_user} icon={Shield} />
      </div>

      <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{t('Idioma')}</h3>
            <p className="text-xs text-slate-500">{t('Selecciona el idioma de la interfaz del ERP.')}</p>
          </div>
          <LanguageSelector />
        </div>
      </div>

      <ModuleSettingsPanel />

      {isSuperUser && (
        <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">{t('Configuración monetaria')}</h3>
              <p className="text-xs text-slate-500">{t(
                'Define la moneda de visualización y tipo de cambio CLP/USD para reportes y dashboards.'
              )}</p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-600">{t('Moneda de visualización')}</label>
              <select
                value={preferences?.displayCurrency || 'USD'}
                onChange={(event) =>
                  updatePreferences({ displayCurrency: event.target.value === 'CLP' ? 'CLP' : 'USD' })
                }
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 bg-white"
              >
                <option value="USD">{t('USD')}</option>
                <option value="CLP">{t('CLP')}</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-600">{t('Tipo de cambio (CLP por 1 USD)')}</label>
              <input
                type="number"
                min="1"
                step="1"
                value={String(preferences?.fxUsdClp ?? 950)}
                onChange={(event) => updatePreferences({ fxUsdClp: Number(event.target.value || 0) })}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
              />
              <p className="text-xs text-slate-400">{t('Ejemplo: 950 (si 1 USD = 950 CLP).')}</p>
            </div>

            <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5">
              <p className="text-sm font-semibold text-slate-800">{t('Impacto')}</p>
              <p className="mt-1 text-xs text-slate-500">{t(
                'Se aplica a compras mensuales del Dashboard y conversiones de montos entre CLP y USD.'
              )}</p>
            </div>
          </div>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="bg-white border border-slate-100 rounded-3xl p-8 grid grid-cols-1 md:grid-cols-2 gap-6 shadow-sm"
      >
        <div className="col-span-full flex items-center gap-3">
          <UserPlus className="w-5 h-5 text-neutral-400" />
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{t('Crear nuevo usuario')}</h3>
            <p className="text-xs text-slate-400">{t(
              'El usuario recibirá la contraseña temporal `admin` para su primer acceso.'
            )}</p>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-semibold text-slate-600">{t('Nombre completo')}</label>
          <input
            value={formState.name}
            onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
            required
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-semibold text-slate-600">{t('Correo corporativo')}</label>
          <div className="relative">
            <Mail className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="email"
              value={formState.email}
              onChange={(event) => setFormState((prev) => ({ ...prev, email: event.target.value }))}
              className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-semibold text-slate-600">{t('Área / Departamento')}</label>
          <div className="relative">
            <Building className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              value={formState.department}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, department: event.target.value }))
              }
              className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
              placeholder={t('Abastecimiento, Control de Gestión, etc.')}
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-semibold text-slate-600">{t('Teléfono')}</label>
          <input
            value={formState.phone}
            onChange={(event) => setFormState((prev) => ({ ...prev, phone: event.target.value }))}
            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
            placeholder="+56 9..."
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-semibold text-slate-600">{t('Rol')}</label>
          <div className="relative">
            <select
              value={formState.role}
              onChange={(event) => setFormState((prev) => ({ ...prev, role: event.target.value }))}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl appearance-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900"
            >
              {roleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.label)}
                </option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2" />
          </div>
        </div>

        <div className="col-span-full flex justify-end">
          <Button type="submit" variant="primary">{t('Crear usuario')}</Button>
        </div>
      </form>

      <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">{t('Usuarios registrados')}</h3>
          <p className="text-xs text-slate-400">{t('Perfiles de prueba por usuario')}</p>
        </div>
        <DataTable
          data={users}
          searchPlaceholder={t('Buscar usuario...')}
          columns={[
            { key: 'name', label: 'Nombre', sortable: true },
            { key: 'email', label: 'Correo', sortable: true },
            { key: 'department', label: 'Área', sortable: true, render: (value) => value || '—' },
            {
              key: 'role',
              label: 'Rol',
              render: (value, user) => (
                <select
                  value={value || 'comprador'}
                  onChange={(event) => updateUserRole(user.id, event.target.value)}
                  disabled={!isSuperUser}
                  onClick={(event) => event.stopPropagation()}
                  className="px-2 py-1.5 border border-slate-200 rounded-lg text-xs"
                >
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )
            }
          ]}
        />
      </div>

      <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{t('Proyectos (edición)')}</h3>
            <p className="text-xs text-slate-500">{t(
                'Ajusta nombre, PM y coordenadas. Las coordenadas son las que alimentan el mapa de'
              )} <span className="font-semibold">{t('Desarrollo')}</span>.
                          </p>
          </div>
          <div className="text-xs text-slate-500">{t(
            'Tip: haz click en el mapa para posicionar el marcador y arrástralo para afinar la ubicación.'
          )}</div>
        </div>

        {parks.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">{t(
            'Aún no hay proyectos. Crea uno en “Setup inicial” para habilitar el resto del flujo.'
          )}</div>
        ) : (
          <DataTable
            data={parks}
            searchPlaceholder={t('Buscar proyecto...')}
            columns={[
              { key: 'name', label: 'Nombre', sortable: true },
              { key: 'code', label: 'Código', sortable: true, render: (value) => value || '—' },
              {
                key: 'managerId',
                label: 'PM',
                render: (value) => users.find((u) => u.id === value)?.name || '—'
              },
              {
                key: 'id',
                label: 'Acciones',
                render: (_, park) => (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenEditPark(park.id);
                    }}
                  >
                    Editar
                  </Button>
                )
              }
            ]}
          />
        )}
      </div>

      {editParkOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl rounded-3xl bg-white border border-slate-200 shadow-xl p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-lg font-bold text-slate-900">{t('Editar proyecto')}</p>
                <p className="text-xs text-slate-500">{t(
                  'Cambios críticos para el flujo: PM y coordenadas (para mapa y control territorial).'
                )}</p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setEditParkOpen(false);
                  setEditParkError('');
                }}
              >{t('Cerrar')}</Button>
            </div>

            {editParkError && (
              <div className="mt-4 rounded-2xl border border-danger-100 bg-danger-50 p-4 text-sm text-danger-700">
                {t(editParkError)}
              </div>
            )}

            <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-600">{t('Nombre')}</label>
                    <input
                      value={editParkForm.name}
                      onChange={(e) => setEditParkForm((p) => ({ ...p, name: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-600">{t('Código')}</label>
                    <input
                      value={editParkForm.code}
                      onChange={(e) => setEditParkForm((p) => ({ ...p, code: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600">{t('Project Manager (PM)')}</label>
                  <select
                    value={editParkForm.managerId}
                    onChange={(e) => setEditParkForm((p) => ({ ...p, managerId: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                  >
                    <option value="">{t('(Sin asignar)')}</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {t(user.name)} · {t(user.role)}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-slate-500">{t(
                    'Si el proyecto no tiene PM, el flujo de aprobación de OC queda incompleto (alertas del sistema lo marcarán).'
                  )}</p>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-600">{t('Región')}</label>
                    <input
                      value={editParkForm.region}
                      onChange={(e) => setEditParkForm((p) => ({ ...p, region: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                      placeholder={t('Antofagasta...')}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-600">{t('Comuna')}</label>
                    <input
                      value={editParkForm.commune}
                      onChange={(e) => setEditParkForm((p) => ({ ...p, commune: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                      placeholder={t('María Elena...')}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-600">{t('MW')}</label>
                    <input
                      value={editParkForm.mw}
                      onChange={(e) => setEditParkForm((p) => ({ ...p, mw: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                      placeholder="100"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-600">{t('Lat')}</label>
                    <input
                      value={editParkForm.lat}
                      onChange={(e) => setEditParkForm((p) => ({ ...p, lat: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                      placeholder="-22.46"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-600">{t('Lng')}</label>
                    <input
                      value={editParkForm.lng}
                      onChange={(e) => setEditParkForm((p) => ({ ...p, lng: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                      placeholder="-69.66"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-600">{t('Ubicación (mapa)')}</p>
                <CoordinatePickerMap
                  value={
                    editParkForm.lat && editParkForm.lng
                      ? { lat: Number(editParkForm.lat), lng: Number(editParkForm.lng) }
                      : null
                  }
                  onChange={(coords) => {
                    if (!coords) return;
                    setEditParkForm((prev) => ({
                      ...prev,
                      lat: String(coords.lat.toFixed(6)),
                      lng: String(coords.lng.toFixed(6))
                    }));
                  }}
                  heightClassName="h-80"
                  zoom={9}
                />
                <p className="text-[11px] text-slate-500">{t(
                    'Click para posicionar; arrastra el marcador para ajustar. Esto se verá luego en'
                  )} <span className="font-semibold">{t('Desarrollo > Referencia Geográfica')}</span>.
                                  </p>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setEditParkOpen(false);
                  setEditParkError('');
                }}
              >{t('Cancelar')}</Button>
              <Button
                variant="primary"
                onClick={() => {
                  try {
                    const coordinates =
                      editParkForm.lat && editParkForm.lng
                        ? { lat: Number(editParkForm.lat), lng: Number(editParkForm.lng) }
                        : null;
                    updatePark(editingParkId, {
                      name: editParkForm.name,
                      code: editParkForm.code,
                      managerId: editParkForm.managerId || null,
                      region: editParkForm.region,
                      commune: editParkForm.commune,
                      mw: Number(editParkForm.mw || 0),
                      coordinates
                    });
                    setEditParkOpen(false);
                    setEditParkError('');
                  } catch (error) {
                    setEditParkError(error?.message || 'No fue posible actualizar el proyecto.');
                  }
                }}
              >{t('Guardar cambios')}</Button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{t('Setup inicial (desde cero)')}</h3>
            <p className="text-xs text-slate-500">{t(
              'Crea proyectos, áreas/CECO y categorías para que el flujo PR → OC funcione.'
            )}</p>
          </div>
          {isSuperUser && (
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={() => loadEnergyTemplates()}>{t('Cargar templates energía')}</Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  const ok = window.confirm(
                    'Esto borrará toda la información local (usuarios, proyectos, PR, OC, proveedores, presupuestos). ¿Continuar?'
                  );
                  if (ok) {
                    resetConstruction();
                    resetAppToZero();
                  }
                }}
              >{t('Resetear sistema')}</Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              try {
                const created = createPark({
                  name: parkForm.name,
                  code: parkForm.code,
                  managerId: parkForm.managerId || null,
                  technology: parkForm.technology,
                  region: parkForm.region,
                  commune: parkForm.commune,
                  mw: Number(parkForm.mw || 0),
                  coordinates:
                    parkForm.lat && parkForm.lng
                      ? { lat: Number(parkForm.lat), lng: Number(parkForm.lng) }
                      : null
                });
                setParkForm({ name: '', code: '', managerId: '', technology: 'Solar FV', region: '', commune: '', mw: '', lat: '', lng: '' });
                setAreaForm((prev) => ({ ...prev, parkId: created.id }));
                setPackageForm((prev) => ({ ...prev, parkId: created.id }));
              } catch (error) {
                window.alert(error?.message || 'No fue posible crear el proyecto.');
              }
            }}
            className="border border-slate-200 rounded-2xl p-4 space-y-3 bg-slate-50"
          >
            <p className="text-sm font-semibold text-slate-800">{t('1) Crear Proyecto')}</p>
            <input
              value={parkForm.name}
              onChange={(e) => setParkForm((p) => ({ ...p, name: e.target.value }))}
              placeholder={t('Nombre proyecto')}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
            />
            <input
              value={parkForm.code}
              onChange={(e) => setParkForm((p) => ({ ...p, code: e.target.value }))}
              placeholder={t('Código (opcional)')}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
            />
            <select
              value={parkForm.technology}
              onChange={(e) => setParkForm((p) => ({ ...p, technology: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
            >
              <option>{t('Solar FV')}</option>
              <option>{t('Eólico')}</option>
              <option>{t('BESS')}</option>
              <option>{t('Híbrido')}</option>
            </select>
            <div className="grid grid-cols-2 gap-2">
              <input
                value={parkForm.region}
                onChange={(e) => setParkForm((p) => ({ ...p, region: e.target.value }))}
                placeholder={t('Región')}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
              />
              <input
                value={parkForm.commune}
                onChange={(e) => setParkForm((p) => ({ ...p, commune: e.target.value }))}
                placeholder={t('Comuna')}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
              />
            </div>
            <input
              type="number"
              value={parkForm.mw}
              onChange={(e) => setParkForm((p) => ({ ...p, mw: e.target.value }))}
              placeholder={t('MW (opcional)')}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                value={parkForm.lat}
                onChange={(e) => setParkForm((p) => ({ ...p, lat: e.target.value }))}
                placeholder={t('Lat (opcional)')}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
              />
              <input
                value={parkForm.lng}
                onChange={(e) => setParkForm((p) => ({ ...p, lng: e.target.value }))}
                placeholder={t('Lng (opcional)')}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
              />
            </div>
            <select
              value={parkForm.managerId}
              onChange={(e) => setParkForm((p) => ({ ...p, managerId: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
            >
              <option value="">{t('(Opcional) Asignar PM')}</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {t(user.name)} · {t(user.role)}
                </option>
              ))}
            </select>
            <Button type="submit" variant="primary" className="w-full justify-center">{t('Crear proyecto')}</Button>
          </form>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              try {
                addParkPackage(packageForm.parkId, {
                  code: packageForm.code,
                  name: packageForm.name
                });
                setPackageForm((prev) => ({ ...prev, code: '', name: '' }));
              } catch (error) {
                window.alert(error?.message || 'No fue posible crear el paquete.');
              }
            }}
            className="border border-slate-200 rounded-2xl p-4 space-y-3 bg-slate-50"
          >
            <p className="text-sm font-semibold text-slate-800">{t('2) Crear Paquete / WBS')}</p>
            <select
              value={packageForm.parkId}
              onChange={(e) => setPackageForm((p) => ({ ...p, parkId: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
            >
              <option value="">{t('Selecciona proyecto')}</option>
              {parks.map((park) => (
                <option key={park.id} value={park.id}>
                  {t(park.name)}
                </option>
              ))}
            </select>
            <input
              value={packageForm.code}
              onChange={(e) => setPackageForm((p) => ({ ...p, code: e.target.value }))}
              placeholder={t('Código (Ej: WBS-ELEC)')}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
              disabled={!packageForm.parkId}
            />
            <input
              value={packageForm.name}
              onChange={(e) => setPackageForm((p) => ({ ...p, name: e.target.value }))}
              placeholder={t('Nombre (Ej: BoP Eléctrico)')}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
              disabled={!packageForm.parkId}
            />
            <Button
              type="submit"
              variant="primary"
              className="w-full justify-center"
              disabled={!packageForm.parkId || !packageForm.name}
            >{t('Crear paquete')}</Button>
          </form>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              try {
                addParkArea(areaForm.parkId, {
                  name: areaForm.name,
                  areaManagerId: areaForm.areaManagerId || null,
                  costCenterCode: areaForm.costCenterCode,
                  costCenterName: areaForm.costCenterName,
                  costCenterDescription: areaForm.costCenterDescription
                });
                setAreaForm((prev) => ({
                  ...prev,
                  name: '',
                  areaManagerId: '',
                  costCenterCode: '',
                  costCenterName: '',
                  costCenterDescription: ''
                }));
              } catch (error) {
                window.alert(error?.message || 'No fue posible crear el área.');
              }
            }}
            className="border border-slate-200 rounded-2xl p-4 space-y-3 bg-slate-50"
          >
            <p className="text-sm font-semibold text-slate-800">{t('3) Crear Área + CECO')}</p>
            <select
              value={areaForm.parkId}
              onChange={(e) => setAreaForm((p) => ({ ...p, parkId: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
            >
              <option value="">{t('Selecciona proyecto')}</option>
              {parks.map((park) => (
                <option key={park.id} value={park.id}>
                  {t(park.name)}
                </option>
              ))}
            </select>
            <input
              value={areaForm.name}
              onChange={(e) => setAreaForm((p) => ({ ...p, name: e.target.value }))}
              placeholder={t('Nombre área (Ej: Construcción)')}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
              disabled={!areaForm.parkId}
            />
            <select
              value={areaForm.areaManagerId}
              onChange={(e) => setAreaForm((p) => ({ ...p, areaManagerId: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
              disabled={!areaForm.parkId}
            >
              <option value="">{t('(Opcional) Asignar aprobador área')}</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {t(user.name)} · {t(user.role)}
                </option>
              ))}
            </select>
            <input
              value={areaForm.costCenterCode}
              onChange={(e) => setAreaForm((p) => ({ ...p, costCenterCode: e.target.value }))}
              placeholder={t('CECO código (opcional)')}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
              disabled={!areaForm.parkId}
            />
            <input
              value={areaForm.costCenterName}
              onChange={(e) => setAreaForm((p) => ({ ...p, costCenterName: e.target.value }))}
              placeholder={t('CECO nombre (opcional)')}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
              disabled={!areaForm.parkId}
            />
            <Button
              type="submit"
              variant="primary"
              className="w-full justify-center"
              disabled={!areaForm.parkId || !areaForm.name}
            >{t('Crear área + CECO')}</Button>
          </form>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              try {
                createCategory(categoryForm);
                setCategoryForm({ name: '', type: 'material', budgetCategory: 'otros' });
              } catch (error) {
                window.alert(error?.message || 'No fue posible crear la categoría.');
              }
            }}
            className="border border-slate-200 rounded-2xl p-4 space-y-3 bg-slate-50"
          >
            <p className="text-sm font-semibold text-slate-800">{t('4) Crear Categoría (OC/Presupuesto)')}</p>
            <input
              value={categoryForm.name}
              onChange={(e) => setCategoryForm((p) => ({ ...p, name: e.target.value }))}
              placeholder={t('Nombre categoría (Ej: Servicios Técnicos)')}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
            />
            <select
              value={categoryForm.type}
              onChange={(e) => setCategoryForm((p) => ({ ...p, type: e.target.value }))}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
            >
              <option value="material">{t('Material')}</option>
              <option value="servicio">{t('Servicio')}</option>
            </select>
            <input
              value={categoryForm.budgetCategory}
              onChange={(e) => setCategoryForm((p) => ({ ...p, budgetCategory: e.target.value }))}
              placeholder={t('Budget key (Ej: subcontratos, servicios_menores, otros)')}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
            />
            <Button
              type="submit"
              variant="primary"
              className="w-full justify-center"
              disabled={!categoryForm.name}
            >{t('Crear categoría')}</Button>
          </form>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="border border-slate-200 rounded-2xl p-4">
            <p className="text-sm font-semibold text-slate-800">{t('Proyectos creados')}</p>
            {parks.length === 0 ? (
              <p className="text-xs text-slate-500 mt-2">{t('Aún no hay proyectos.')}</p>
            ) : (
              <div className="mt-3 space-y-3">
                {parks.map((park) => (
                  <div key={park.id} className="border border-slate-100 rounded-xl p-3 bg-slate-50">
                    <p className="text-sm font-semibold text-slate-900">
                      {t(park.name)} <span className="text-xs text-slate-500">({t(park.code)})</span>
                    </p>
                    {(park.packages || []).length > 0 && (
                      <p className="text-xs text-slate-500 mt-1">{t('Paquetes:')} {(park.packages || []).map((pkg) => pkg.code || pkg.name).join(', ')}
                      </p>
                    )}
                    {(park.areas || []).length === 0 ? (
                      <p className="text-xs text-slate-500 mt-1">{t('Sin áreas todavía.')}</p>
                    ) : (
                      <ul className="mt-2 text-xs text-slate-600 space-y-1">
                        {(park.areas || []).map((area) => {
                          const ceco = costCenters.find((cc) => cc.id === area.costCenterId);
                          return (
                            <li key={area.id}>
                              {t(area.name)} {t('· CECO:')} {t(ceco?.code) || t(area.costCenterId) || '—'}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border border-slate-200 rounded-2xl p-4">
            <p className="text-sm font-semibold text-slate-800 mb-3">{t('Categorías creadas')}</p>
            {categories.length === 0 ? (
              <p className="text-xs text-slate-500">{t('Aún no hay categorías.')}</p>
            ) : (
              <DataTable
                data={categories}
                searchPlaceholder={t('Buscar categoría...')}
                columns={[
                  { key: 'name', label: 'Nombre', sortable: true },
                  { key: 'type', label: 'Tipo', sortable: true },
                  {
                    key: 'budgetCategory',
                    label: 'Budget key',
                    render: (value) => value || 'otros'
                  }
                ]}
              />
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">{t('Autorización de módulos y sub módulos')}</h3>
          <p className="text-xs text-slate-400">{t('Solo super_user puede modificar accesos')}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4">
          <div className="border border-slate-100 rounded-2xl p-4 bg-slate-50">
            <label className="text-xs text-slate-500 uppercase">{t('Usuario')}</label>
            <select
              value={selectedUserId}
              onChange={(event) => setSelectedUserId(event.target.value)}
              className="mt-2 w-full px-3 py-2 border border-slate-200 rounded-xl text-sm"
            >
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {t(user.name)} · {t(user.profile) || 'operativo'}
                </option>
              ))}
            </select>
          </div>
          <div className="border border-slate-100 rounded-2xl p-4">
            {selectedUser ? (
              <div className="space-y-4">
                {pagesCatalog.map((group) => (
                  <div key={group.module}>
                    <p className="text-xs font-semibold uppercase text-slate-500 mb-2">{t(group.module)}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {group.pages.map((page) => (
                        <label key={page} className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={selectedAccess.has(page)}
                            disabled={!isSuperUser}
                            onChange={(event) => {
                              const next = new Set(selectedAccess);
                              if (event.target.checked) next.add(page);
                              else next.delete(page);
                              updateUserAllowedPages(selectedUser.id, Array.from(next));
                            }}
                            className="h-4 w-4"
                          />
                          {t(page)}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                {isSuperUser && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => updateUserAllowedPages(selectedUser.id, allPages)}
                  >{t('Habilitar todo')}</Button>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500">{t('Selecciona un usuario para gestionar accesos.')}</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-slate-600" />
            <h3 className="text-lg font-semibold text-slate-900">{t('Bitácora de auditoría interna')}</h3>
          </div>
          <p className="text-xs text-slate-400">{t(totalAudits)} {t('eventos registrados')}</p>
        </div>
        <DataTable
          data={auditTrail}
          searchPlaceholder={t('Buscar evento...')}
          tableMaxHeight="320px"
          emptyTitle={t('Aún no hay eventos registrados')}
          emptyDescription={t('Las acciones de usuarios quedarán registradas aquí.')}
          columns={[
            { key: 'action', label: 'Acción', sortable: true },
            { key: 'entity', label: 'Entidad', sortable: true },
            { key: 'date', label: 'Fecha', sortable: true },
            { key: 'result', label: 'Resultado', sortable: true }
          ]}
        />
      </div>
    </div>
  );
};

export default Administration;
